import { Component } from '@theme/component';

/**
 * Floating Video Widget Component
 * A video widget that floats on the page, initially minimized in the corner.
 * When clicked, it expands with mute, fullscreen, close controls and an Add to Cart button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} minimizedView - The minimized view container
 * @property {HTMLElement} expandedView - The expanded view container
 * @property {HTMLElement} miniVideoContainer - Mini video container
 * @property {HTMLButtonElement} expandTrigger - Button to expand the widget
 * @property {HTMLButtonElement} closeMini - Button to close minimized widget
 * @property {HTMLButtonElement} closeBtn - Button to collapse expanded widget
 * @property {HTMLButtonElement} muteBtn - Mute toggle button
 * @property {HTMLButtonElement} fullscreenBtn - Fullscreen toggle button
 * @property {HTMLButtonElement} playPauseBtn - Play/pause toggle button
 * @property {HTMLElement} videoWrapper - Video wrapper element
 *
 * @extends Component<Refs>
 */
class FloatingVideoWidget extends Component {
  /** @type {boolean} */
  isExpanded = false;

  /** @type {boolean} */
  isPlaying = true;

  /** @type {boolean} */
  isMuted = true;

  /** @type {boolean} */
  isFullscreen = false;

  /** @type {boolean} */
  isHidden = false;

  /** @type {number | undefined} */
  #timestampInterval;

  #abortController = new AbortController();

  /**
   * Get mini video element
   * @returns {HTMLVideoElement | null}
   */
  get miniVideo() {
    return this.querySelector('.js-mini-video');
  }

  /**
   * Get main video element
   * @returns {HTMLVideoElement | null}
   */
  get mainVideo() {
    return this.querySelector('.js-main-video');
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const initialState = this.dataset.initialState || 'minimized';

    // Set initial state
    if (initialState === 'hidden') {
      this.isHidden = true;
      this.setAttribute('hidden', '');
    } else if (initialState === 'expanded') {
      this.isExpanded = true;
      this.refs.minimizedView?.setAttribute('hidden', '');
      this.refs.expandedView?.removeAttribute('hidden');
    }

    // Hold the widget back until the visitor has scrolled past a fraction of the first
    // viewport, so it doesn't cover the hero image the moment the page paints. Opt-in via
    // data-reveal-after-scroll (a fraction of viewport height); absent = show immediately,
    // which keeps every existing placement unchanged.
    this.#setUpScrollReveal({ signal });

    // Perf: defer the mini video off the critical path (loads on page-idle, not on paint)
    this.#scheduleMiniVideoLoad();

    // Update timestamp
    this.#startTimestampUpdates();

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', this.#handleFullscreenChange, { signal });
    document.addEventListener('webkitfullscreenchange', this.#handleFullscreenChange, { signal });

    // Handle escape key to close
    document.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  // data-auto-hide (seconds): fade the minimized bubble out again that long after it appears,
  // so it doesn't sit over the page forever. Never hides while the visitor has it open.
  #startAutoHide(signal) {
    const seconds = parseFloat(this.dataset.autoHide);
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    const timer = setTimeout(() => {
      if (this.isExpanded) return;
      this.classList.add('floating-video-widget--scroll-hidden');
    }, seconds * 1000);
    signal?.addEventListener('abort', () => clearTimeout(timer));
  }

  #setUpScrollReveal({ signal }) {
    // Two triggers, selector first: data-reveal-selector waits until that element's top edge
    // enters the viewport (e.g. a section heading further down the page) — or, with
    // data-reveal-selector-past, until its bottom edge has left the top of the viewport (the
    // hair-oil page: only after the review tiles, Nino 27 Oct 2026); otherwise
    // data-reveal-after-scroll waits for a fraction of the first viewport.
    const selector = this.dataset.revealSelector;
    const past = this.dataset.revealSelectorPast === 'true';
    let target = selector ? document.querySelector(selector) : null;
    // A match with no box (display:none at this breakpoint) reports top 0 and would
    // reveal on load — treat it as unmatched.
    if (target && target.getClientRects().length === 0) target = null;
    const raw = parseFloat(this.dataset.revealAfterScroll);
    if (!target && (!Number.isFinite(raw) || raw <= 0)) {
      this.classList.remove('floating-video-widget--scroll-hidden'); /* painted hidden by the section when a trigger is set */
      this.#startAutoHide(signal);
      return;
    }

    // Never let the widget be permanently unreachable on a short page.
    const threshold = () => Math.min(window.innerHeight * raw, Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
    const reached = () => (target
      ? (past ? target.getBoundingClientRect().bottom < 0 : target.getBoundingClientRect().top < window.innerHeight)
      : window.scrollY >= threshold());

    this.classList.add('floating-video-widget--scroll-hidden');

    let revealed = false;
    const check = () => {
      if (revealed) return;
      if (!reached()) return;
      revealed = true;
      this.classList.remove('floating-video-widget--scroll-hidden');
      window.removeEventListener('scroll', check);
      this.#startAutoHide(signal);
    };

    window.addEventListener('scroll', check, { passive: true, signal });
    window.addEventListener('resize', check, { passive: true, signal });
    // Covers a reload partway down the page, where no scroll event ever fires.
    check();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#timestampInterval) clearInterval(this.#timestampInterval);
  }

  /**
   * Defers mini-video load off the critical path: waits for page `load`, then browser idle,
   * then loads + fades in the mini video. The <video> is `preload="none"`, so nothing is
   * fetched until play() is called here — keeping the ~5 MB video off first paint.
   */
  #scheduleMiniVideoLoad() {
    const start = () => {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
      idle(() => this.#activateMiniVideo(), { timeout: 3000 });
    };
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true, signal: this.#abortController.signal });
    }
  }

  /**
   * Loads and plays the mini video, fading it in over the poster once it can play.
   */
  #activateMiniVideo() {
    // If the widget booted already expanded, prime the main video instead.
    if (this.isExpanded) {
      this.#playMainVideo();
      return;
    }
    const miniVideo = this.miniVideo;
    if (!miniVideo) return;

    const reveal = () => this.classList.add('floating-video-widget--video-loaded');
    if (miniVideo.readyState >= 2) {
      reveal();
    } else {
      miniVideo.addEventListener('canplay', reveal, { once: true, signal: this.#abortController.signal });
    }
    // play() kicks off the fetch (preload="none"); muted, so autoplay is permitted.
    miniVideo.play().catch(() => reveal());
  }

  /**
   * Ensures the main (expanded) video is loaded, then plays it. Safe to call before it has
   * loaded — main video is `preload="none"`, so this triggers its first fetch on expand.
   * @param {number} [startTime] - seconds to seek to before playing
   */
  #playMainVideo(startTime = 0) {
    const mainVideo = this.mainVideo;
    if (!mainVideo) return;
    mainVideo.muted = this.isMuted;
    const startPlayback = () => {
      try {
        if (startTime) mainVideo.currentTime = startTime;
      } catch (e) {
        /* seeked before metadata ready — ignore */
      }
      mainVideo.play().catch(() => {});
    };
    if (mainVideo.readyState >= 1) {
      startPlayback();
    } else {
      mainVideo.addEventListener('loadedmetadata', startPlayback, { once: true, signal: this.#abortController.signal });
      mainVideo.load();
    }
  }

  /**
   * Updates timestamp display (no longer used but kept for potential future use)
   */
  #startTimestampUpdates() {
    // Timestamp display removed from minimized view
  }

  /**
   * Handles expand button click
   */
  handleExpand() {
    this.isExpanded = true;
    
    this.refs.minimizedView?.setAttribute('hidden', '');
    this.refs.expandedView?.removeAttribute('hidden');

    // Pause mini video and start the main video at the same position.
    // Main video is preload="none", so #playMainVideo lazy-loads it on this first expand.
    const miniVideo = this.miniVideo;
    const startTime = miniVideo ? miniVideo.currentTime : 0;
    miniVideo?.pause();
    this.#playMainVideo(startTime);
    this.isPlaying = true;
    this.#updatePlayPauseIcon();
  }

  /**
   * Handles collapse button click
   */
  handleCollapse() {
    // Exit fullscreen if active
    if (this.isFullscreen) {
      this.#exitFullscreen();
    }

    this.isExpanded = false;
    
    this.refs.expandedView?.setAttribute('hidden', '');
    this.refs.minimizedView?.removeAttribute('hidden');

    // Pause main video and resume mini video at same position
    const miniVideo = this.miniVideo;
    const mainVideo = this.mainVideo;
    if (mainVideo && miniVideo) {
      const currentTime = mainVideo.currentTime;
      mainVideo.pause();
      miniVideo.currentTime = currentTime;
      miniVideo.play().catch(() => {});
    }
  }

  /**
   * Handles close mini button click
   */
  handleCloseMini() {
    this.isHidden = true;
    this.setAttribute('hidden', '');
    
    // Pause videos
    this.miniVideo?.pause();
    this.mainVideo?.pause();
  }

  /**
   * Shows the widget again
   */
  show() {
    this.isHidden = false;
    this.removeAttribute('hidden');

    // Route through the lazy-load paths so the video loads + reveals even if it was
    // never activated before being hidden.
    if (this.isExpanded) {
      this.#playMainVideo();
    } else {
      this.#activateMiniVideo();
    }
  }

  /**
   * Handles replay button click
   */
  handleReplay() {
    const mainVideo = this.mainVideo;
    if (mainVideo) {
      mainVideo.currentTime = 0;
      mainVideo.play().catch(() => {});
      this.isPlaying = true;
      this.classList.remove('floating-video-widget--paused');
      this.#updatePlayPauseIcon();
    }
  }

  /**
   * Handles mute toggle
   */
  handleToggleMute() {
    this.isMuted = !this.isMuted;
    
    const mainVideo = this.mainVideo;
    const muteBtn = this.refs.muteBtn;
    if (mainVideo) {
      mainVideo.muted = this.isMuted;
    }

    if (muteBtn) {
      const volumeOn = muteBtn.querySelector('.icon-volume-on');
      const volumeOff = muteBtn.querySelector('.icon-volume-off');
      
      if (this.isMuted) {
        volumeOn?.classList.add('hidden');
        volumeOff?.classList.remove('hidden');
      } else {
        volumeOn?.classList.remove('hidden');
        volumeOff?.classList.add('hidden');
      }
    }
  }

  /**
   * Handles fullscreen toggle
   */
  handleFullscreen() {
    if (this.isFullscreen) {
      this.#exitFullscreen();
    } else {
      this.#enterFullscreen();
    }
  }

  /**
   * Enters fullscreen mode
   */
  #enterFullscreen() {
    this.isFullscreen = true;
    this.classList.add('floating-video-widget--fullscreen');
    
    // Try native fullscreen first
    if (this.requestFullscreen) {
      this.requestFullscreen().catch(() => {
        // Fallscreen failed, use CSS fullscreen
      });
    } else if (/** @type {any} */ (this).webkitRequestFullscreen) {
      /** @type {any} */ (this).webkitRequestFullscreen();
    }
  }

  /**
   * Exits fullscreen mode
   */
  #exitFullscreen() {
    this.isFullscreen = false;
    this.classList.remove('floating-video-widget--fullscreen');
    
    if (document.fullscreenElement || /** @type {any} */ (document).webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (/** @type {any} */ (document).webkitExitFullscreen) {
        /** @type {any} */ (document).webkitExitFullscreen();
      }
    }
  }

  /**
   * Handles fullscreen change event
   */
  #handleFullscreenChange = () => {
    const isFullscreen = !!(document.fullscreenElement || /** @type {any} */ (document).webkitFullscreenElement);
    
    if (!isFullscreen && this.isFullscreen) {
      this.isFullscreen = false;
      this.classList.remove('floating-video-widget--fullscreen');
    }
  };

  /**
   * Handles play/pause toggle
   */
  handleTogglePlayPause() {
    const mainVideo = this.mainVideo;
    
    if (!mainVideo) return;

    if (this.isPlaying) {
      mainVideo.pause();
      this.isPlaying = false;
      this.classList.add('floating-video-widget--paused');
    } else {
      mainVideo.play().catch(() => {});
      this.isPlaying = true;
      this.classList.remove('floating-video-widget--paused');
    }

    this.#updatePlayPauseIcon();
  }

  /**
   * Updates play/pause icon visibility
   */
  #updatePlayPauseIcon() {
    const { playPauseBtn } = this.refs;
    if (playPauseBtn) {
      const playIcon = playPauseBtn.querySelector('.icon-play');
      const pauseIcon = playPauseBtn.querySelector('.icon-pause');
      
      if (this.isPlaying) {
        playIcon?.classList.add('hidden');
        pauseIcon?.classList.remove('hidden');
      } else {
        playIcon?.classList.remove('hidden');
        pauseIcon?.classList.add('hidden');
      }
    }
  }

  /**
   * Handles escape key
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      if (this.isFullscreen) {
        this.#exitFullscreen();
      } else if (this.isExpanded) {
        this.handleCollapse();
      }
    }
  };

  // Add-to-cart is NOT handled here: the button's on:click resolves to the nested
  // add-to-cart-component, and the AJAX submit runs through product-form-component's
  // handleSubmit (wired via on:submit in the section) — see docs/repo-map.md.
}

if (!customElements.get('floating-video-widget')) {
  customElements.define('floating-video-widget', FloatingVideoWidget);
}

